import { useCallback, useEffect, useState } from 'react';

const DISMISSED_KEY = 'pwa-install-dismissed';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  // iOS Safari's non-standard flag for "launched from home screen".
  (window.navigator as Navigator & { standalone?: boolean }).standalone === true;

const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);

/**
 * Drives the "install this app" prompt: captures Chrome/Android's
 * `beforeinstallprompt` event for a native install flow, and separately
 * detects iOS Safari (which has no such event) so the caller can show
 * manual "Add to Home Screen" instructions instead.
 *
 * Dismissal is persisted in `sessionStorage` (not `localStorage`) — so a
 * "not now" only silences the prompt for the current browser session; it
 * reappears next time the app is opened in a fresh session, until it's
 * actually installed (`isStandalone()`), at which point it stops for good.
 */
export const usePwaInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISSED_KEY) === '1');

  useEffect(() => {
    if (isStandalone()) return;

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setDismissed(true);
      sessionStorage.setItem(DISMISSED_KEY, '1');
    };
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const dismiss = useCallback(() => {
    setDismissed(true);
    sessionStorage.setItem(DISMISSED_KEY, '1');
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  const canPromptInstall = deferredPrompt !== null;
  const showIosInstructions = !canPromptInstall && isIos() && !isStandalone();
  const shouldShowInstallModal =
    !(dismissed || isStandalone()) && (canPromptInstall || showIosInstructions);

  return { shouldShowInstallModal, canPromptInstall, showIosInstructions, promptInstall, dismiss };
};
